import { useAppStore } from '@modules/app/store/useAppStore';
import { FloorplanInteractionMode, Module } from '@modules/app/types';

import { getCurrentKeyboardContext } from './getCurrentKeyboardContext';
import { IKeyMap } from './keyMaps/IKeyMap';
import { KeyboardContext, KeyCode, Shortcut } from './types';
import { CanvasLayoutKeyMap } from './keyMaps/CanvasLayoutKeyMap';
import { CanvasSetupKeyMap } from './keyMaps/CanvasSetupKeyMap';
import { PropertyPanelKeyMap } from './keyMaps/PropertyPanelKeyMap';
import { ReadOnlyKeyMap } from './keyMaps/ReadOnlyKeyMap';

export default class KeyboardUtil {
  private static instance: KeyboardUtil;
  private activeModule: Module | undefined;
  private mode: FloorplanInteractionMode | undefined;

  public static initialize(appStore: typeof useAppStore) {
    KeyboardUtil.instance = new KeyboardUtil();

    const { activeModule, mode } = appStore.getState();

    KeyboardUtil.instance.activateModuleKeyboardEvents(activeModule);
    KeyboardUtil.instance.mode = mode;
  }

  public static getInstance(): KeyboardUtil {
    if (!KeyboardUtil.instance) {
      throw new Error('KeyboardUtil not yet initialized. Call KeyboardUtil.initialize() first.');
    }

    return KeyboardUtil.instance;
  }

  public activateModuleKeyboardEvents(module: Module): void {
    this.activeModule = module;
  }

  public setMode(mode: FloorplanInteractionMode): void {
    this.mode = mode;
  }

  public handleKeyPress(event: KeyboardEvent): void {
    const shortcut = this.eventToShortcut(event);

    if (event.repeat && !this.shouldAllowRepeatShortcut(shortcut)) return;

    if (this.shouldPreventBrowserShortcut(shortcut)) {
      event.preventDefault();
    }

    const keyMap = this.resolveKeyMap();
    if (!keyMap) return;

    keyMap.handleKeyPress(shortcut);
  }

  private shouldAllowRepeatShortcut(shortcut: Shortcut): boolean {
    if (!shortcut.ctrl) return false;

    return shortcut.code === 'KeyV';
  }

  private shouldPreventBrowserShortcut(shortcut: Shortcut): boolean {
    if (!shortcut.ctrl) return false;

    return ['KeyS', 'KeyZ', 'KeyY', 'KeyD', 'KeyA'].includes(shortcut.code);
  }

  private resolveKeyMap(): IKeyMap | undefined {
    if (this.mode === 'SELECT_ONLY' || this.mode === 'READONLY') {
      return new ReadOnlyKeyMap();
    }

    switch (this.activeModule) {
      case 'canvas_layout':
        return this.handleCanvasLayoutContext(getCurrentKeyboardContext());
      case 'canvas_setup':
        return new CanvasSetupKeyMap();
      default:
        return undefined;
    }
  }

  private handleCanvasLayoutContext(context: KeyboardContext): IKeyMap | undefined {
    switch (context) {
      case 'canvas':
        return new CanvasLayoutKeyMap();

      case 'propertyPanel':
        return new PropertyPanelKeyMap();

      default:
        return undefined;
    }
  }

  private eventToShortcut(e: KeyboardEvent): Shortcut {
    const ctrl = e.ctrlKey || e.metaKey;

    return {
      code: e.code as KeyCode,
      ctrl,
      shift: e.shiftKey,
      alt: e.altKey,
      meta: e.metaKey,
    };
  }
}
